import { universalSearchOnFocus } from "app/actions/universalSearchActions";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import {
    AdUnit,
    IndexCell,
    KeywordAnalysisPosition,
    KeywordsList,
    ReferringCategoryCell,
    SimpleLinkCell,
} from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";

import { translate } from "app/filters/i18nFilter";
import { adsTargetURL } from "app/filters/ngFilters";
import ProductAdsTableTop from "../../../pages/keyword-analysis/ProductAdsTableTop";
import queryString from "query-string";
import { connect } from "react-redux";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { swSettings } from "app/common/services/swSettings";
import { TabListStyled } from "app/components/keyword-research/landing-pages/styledComponents";
import { Tabs, Tab, TabPanel } from "@similarweb/ui-components";
import { useState } from "react";
import { useCurrentStateAndParams } from "@uirouter/react";
import { PaidSearchContainer } from "app/components/React/PlaResearch/StyledComponents";
import { EAdsTabs } from "./type";
import { onCategorySelected } from "app/common/keyword-research/keyword-search-ads/categoryOptionsUtils";
import { hasDigitalMarketingPermission, hasMarketResearchPermission } from "app/components/SideBar/utils";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

const ProductAdsTable = (props) => {
    const {
        isWWW,
        key,
        keyword,
        selectedDomain,
        country,
        duration,
        webSource,
        selectedCategory = "",
        search: searchParams = "",
        orderBy = "",
    } = proNavigator.getParams();
    const type = props?.type ?? "all";

    const {
        serverApi,
        showCategorySelector,
        showDomainSelector,
        tableColumns,
        excelApi,
        getCurrentTerm,
        useDefaultSorting = true,
        innerLinkPageDest,
    } = props;
    let { field = "Position", sortDirection = "asc" } = props;
    const search = decodeURIComponent(searchParams);
    const filterParam = `Title;contains;"${search}"`;
    // in order to merge both keyword ads and website ads i used the word 'term' instead of 'keyword' or 'website'
    const currentTerm = getCurrentTerm(keyword, selectedDomain, key);
    if (!currentTerm) {
        return null;
    }
    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;
    const params = {
        country,
        webSource,
        from,
        to,
        includeSubDomains: isWWW === "*",
        isWindow,
        timeGranularity: "Monthly",
        keys: currentTerm,
        atype: type,
        pageSize: 20,
        filter: filterParam,
    } as any;
    if (selectedCategory) {
        params.category = selectedCategory;
    }
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const TrackAdLangPageClick = (adType) => (tableOptions, category, value, action) => {
        switch (category) {
            case "External Link": // todo
                return allTrackers.trackEvent(tableOptions, category, `${adType}/${value}`, action);
            case "upgrade": // todo
                return allTrackers.trackEvent(tableOptions, category, `${adType}/${value}`, action);
        }
    };
    if (orderBy !== "") {
        [field, sortDirection] = orderBy.split(" ");
    }
    params.sort = field;
    params.asc = sortDirection === "asc";
    if (useDefaultSorting) {
        params.orderBy = `${field} ${sortDirection}`;
    } else {
        params.orderBy = field + " " + (params.asc ? "asc" : "desc");
    }
    const genericColumns = [
        {
            fixed: true,
            cellComponent: IndexCell,
            disableHeaderCellHover: true,
            sortable: false,
            width: 46,
            cellClass: "kwa-ads-index",
            isResizable: false,
        },
        {
            field: "AdDetails",
            displayName: translate("keyword.analysis.ads.table.columns.addetails.title"),
            tooltip: `keyword.analysis.ads.table.columns.addetails.title.tooltip`,
            cellComponent: AdUnit,
            width: 430,
            showTotalCount: true,
            cellClass: "kwa-ads-ads",
            sortable: false,
            isResizable: false,
        },
        {
            field: "Domain",
            displayName: translate("keyword.analysis.ads.table.columns.domain.title"),
            tooltip: "keyword.analysis.ads.table.columns.domain.title.tooltip",
            cellComponent: ({ value, row }) => {
                const props = {
                    domain: value,
                    icon: row.Favicon,
                    internalLink: proNavigator.href("organicsearch_websites_v2_traffic_keywords", {
                        key: value,
                        country,
                        duration,
                        webSource,
                        isWWW,
                        selectedPageTab: EKeywordTabs[EKeywordTabs.Paid],
                    }),
                    trackInternalLink: (e) => {
                        e.stopPropagation();
                        allTrackers.trackEvent("Internal Link", "click", `Table/${value}`);
                    },
                    externalLink: `http://${value}`,
                    trackExternalLink: (e) => {
                        e.stopPropagation();
                        allTrackers.trackEvent("External Link", "click", `Table/${value}`);
                    },
                    hideTrackButton: true,
                };
                return <CoreWebsiteCell {...props} />;
            },
            minWidth: 152,
            sortable: false,
        },
        {
            field: "Page",
            displayName: translate("keyword.analysis.ads.table.columns.page.title"),
            tooltip: "keyword.analysis.ads.table.columns.page.title.tooltip",
            cellClass: "kwa-ads-landing",
            minWidth: 330,
            sortable: false,
            cellComponent: (props) => {
                const { AdDetails } = props.row;
                const { Type, Title } = AdDetails;
                const value = Title === "grid.upgrade" ? undefined : adsTargetURL()(AdDetails);
                if (!value) {
                    return <span>{"-"}</span>;
                } else {
                    return <SimpleLinkCell {...{ ...props, value, track: TrackAdLangPageClick(Type) }} />;
                }
            },
        },
        {
            field: "Keywords",
            displayName: translate("keyword.analysis.ads.table.columns.keywords.title"),
            tooltip: `keyword.analysis.ads.table.columns.keywords.title.tooltip`,
            cellComponent: (props) => {
                const getLink = (value: string) => {
                    return proNavigator.href(innerLinkPageDest ?? proNavigator.current().name, {
                        ...params,
                        keyword: value.trim(),
                        duration: "3m",
                    });
                };
                return (
                    <KeywordsList
                        {...props}
                        getLink={hasMarketResearchPermission() || hasDigitalMarketingPermission() ? getLink : undefined}
                        showTotalCount={true}
                    />
                );
            },
            cellClass: "kwa-ads-keywords",
            sortable: false,
            width: 200,
        },
        {
            field: "Position",
            displayName: translate("keyword.analysis.ads.table.columns.position.title"),
            tooltip: "keyword.analysis.ads.table.columns.position.title.tooltip",
            minWidth: 100,
            sortable: true,
            cellClass: "kwa-ads-position",
            cellComponent: KeywordAnalysisPosition,
        },
        {
            field: "Category",
            displayName: translate("keyword.analysis.ads.table.columns.category.title"),
            tooltip: "keyword.analysis.ads.table.columns.category.title.tooltip",
            cellComponent: (props) => {
                const { value } = props;
                const getLink = (value) => {
                    return proNavigator.href(proNavigator.current().name, {
                        ...params,
                        selectedCategory: value.replace("/", "~"),
                    });
                };
                return (
                    <ReferringCategoryCell
                        {...props}
                        value={value === "Unknown" ? "-" : value}
                        getLink={getLink}
                    />
                );
            },
            minWidth: 120,
            sortable: false,
            visible: false,
        },
    ].map((col: any) => {
        const isSorted = col.field === field;
        return {
            ...col,
            visible: col.visible !== false,
            headerComponent: col.headerComponent || DefaultCellHeader,
            isSorted,
            sortDirection,
            isResizable: col.isResizable === false ? false : true,
        };
    });
    // sets the relevant columns for each context
    const columns = genericColumns.filter((column) => tableColumns.includes(column.field) || !column.field);
    const transformData = (data) => {
        return {
            ...data,
            Data: data.Data.map((item) => {
                const { Keywords, Position, NumberOfKeywords, Category, Domain, Favicon, ...AdDetails } = item;
                return {
                    AdDetails,
                    Favicon,
                    country,
                    Category,
                    Domain,
                    Keywords,
                    NumberOfKeywords,
                    Position: typeof Position === "number" ? Math.round(Position) : Position,
                    DestUrl: item.DestUrl,
                };
            }),
        };
    };
    const getExcel = () => {
        if (excelApi) {
            const queryStringParams = queryString.stringify(params);
            return excelApi + queryStringParams;
        } else {
            return null;
        }
    };

    const onSearch = (search) => {
        proNavigator.updateParams({
            search: search || null,
        });
    };

    const onSort = ({ field, sortDirection }) => {
        proNavigator.updateParams({
            orderBy: `${field} ${sortDirection}`,
        });
    };
    const onSelectDomain = (props) => {
        proNavigator.updateParams({
            selectedDomain: props.text || null,
        });
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter Domain/${props?.text || ""}`);
    };
    return (
        <PaidSearchContainer className={`swReactTable-ads-${props.type}`}>
            {
                <SWReactTableWrapper
                    tableOptions={{
                        metric: "KeywordAnalysisAdsTable",
                    }}
                    serverApi={serverApi}
                    tableColumns={columns}
                    initialFilters={params}
                    recordsField="Data"
                    totalRecordsField="TotalCount"
                    transformData={transformData}
                    pageIndent={1}
                    onSort={onSort}
                    preventDefaultOnSort
                    rowsPerPage={20}
                >
                    {(topComponentProps) => (
                        <ProductAdsTableTop
                            {...topComponentProps}
                            excelLink={getExcel}
                            downloadExcelPermitted={downloadExcelPermitted}
                            selectedType={type}
                            selectedCategory={selectedCategory}
                            onCategorySelected={onCategorySelected}
                            onSearch={onSearch}
                            searchValue={search}
                            showCategorySelector={showCategorySelector}
                            showDomainSelector={showDomainSelector}
                            onSelectDomain={onSelectDomain}
                            selectedDomain={currentTerm}
                        />
                    )}
                </SWReactTableWrapper>
            }
        </PaidSearchContainer>
    );
};
const mapStateToProps = (ownProps) => {
    return {
        params: {
            type: ownProps.type,
            contextParams: ownProps.contextParams,
        },
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        universalSearchOnFocus: (searchText) => {
            dispatch(universalSearchOnFocus(searchText));
        },
    };
};

const Connected = connect(mapStateToProps, mapDispatchToProps)(ProductAdsTable);

export const ProductAdsTableKeywordsContext = ({ type, emptyStateTitle, emptyStateSubTitle }) => {
    const contextParams = {
        serverApi: "/widgetApi/KeywordAnalysisDisplayAds/ScrapedAdsByKeywords/Table",
        showCategorySelector: true,
        showDomainSelector: false,
        tableColumns: ["AdDetails", "Domain", "Page", "Keywords", "Position", "Category"],
        getCurrentTerm: (keyword, selectedDomain, key) => {
            return keyword?.startsWith("*") ? keyword?.substring(1) : keyword;
        },
    };
    const { params } = useCurrentStateAndParams();
    const { selectedPageTab } = params;
    const [selectedPageTabIndex, setSelectedPageTabIndex] = useState<EAdsTabs>(
        EAdsTabs[selectedPageTab as keyof typeof EAdsTabs],
    );
    const onPageTabSelect = (index: EAdsTabs) => {
        const selectedTabKey = EAdsTabs[index];
        setSelectedPageTabIndex(index);
        proNavigator.updateParams({ selectedPageTab: selectedTabKey });
        allTrackers.dispatchTrackEvent("Tab", "click", `Change page tab/${selectedTabKey || ""}`);
    };

    return (
        <Tabs
            onSelect={onPageTabSelect}
            selectedIndex={selectedPageTabIndex}
        >
            <TabListStyled size={"lg"}>
                <Tab id={EAdsTabs.Text}>{translate("digitalsuite.keywordAnalysis.ads.page.tab.text")}</Tab>
                <Tab id={EAdsTabs.Product}>{translate("digitalsuite.keywordAnalysis.ads.page.tab.product")}</Tab>
            </TabListStyled>
            <TabPanel>
                <Connected
                    type={"text"}
                    emptyStateTitle={"keyword.analysis.ads.page.empty.title"}
                    emptyStateSubTitle={"keyword.analysis.ads.page.empty.subtitle"}
                    {...contextParams}
                />
            </TabPanel>
            <TabPanel>
                <Connected
                    type="shopping"
                    emptyStateTitle={"keyword.analysis.plaresearch.page.empty.title"}
                    emptyStateSubTitle={"keyword.analysis.plaresearch.page.empty.subtitle"}
                    {...contextParams}
                />
            </TabPanel>
        </Tabs>
    );
};
